import { useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'

import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { Can } from '../Can'
import ClosingReasonModal from '../ClosingReasonModal'
import ConfirmationModal from '../ConfirmationModal'
import TransferTicketModalCustom from '../TransferTicketModalCustom'
import useClosingReasonSettings from '../../hooks/useClosingReasonSettings'

import ScheduleModal from '../ScheduleModal'

const TicketOptionsMenu = ({
  ticket,
  menuOpen,
  handleClose,
  anchorEl,
  showTabGroups
}) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false)
  const isMounted = useRef(true)
  const { user } = useContext(AuthContext)
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [closingReasonModalOpen, setClosingReasonModalOpen] = useState(false)
  const [contactId, setContactId] = useState(null)
  const { shouldRequireClosingReason, hasClosingReasonsForTicket } =
    useClosingReasonSettings()

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${ticket.id}`)
    } catch (err) {
      toastError(err)
    }
  }

  const handleOpenConfirmationModal = e => {
    setConfirmationOpen(true)
    handleClose()
  }

  const handleOpenTransferModal = e => {
    setTransferTicketModalOpen(true)
    handleClose()
  }

  const handleCloseTransferTicketModal = () => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false)
    }
  }

  const handleOpenScheduleModal = () => {
    handleClose()
    setContactId(ticket.contact.id)
    setScheduleModalOpen(true)
  }

  const handleCloseScheduleModal = () => {
    setScheduleModalOpen(false)
    setContactId(null)
  }

  const handleOpenClosingReasonModal = async e => {
    handleClose()

    const isMainAttendant = ticket.userId === user?.id

    // Se existir atendente principal e o usuário atual NÃO é ele, apenas desvincula
    if (ticket.userId && !isMainAttendant) {
      try {
        setLoading(true)
        await api.delete(`/ticket-attendants/${ticket.id}/${user?.id}`)
        if (isMounted.current) {
          setLoading(false)
          history.push('/tickets')
        }
      } catch (err) {
        if (isMounted.current) {
          setLoading(false)
        }
        toastError(err)
      }
      return
    }

    if (
      shouldRequireClosingReason(ticket) &&
      (await hasClosingReasonsForTicket(ticket))
    ) {
      setClosingReasonModalOpen(true)
    } else {
      // Se não requer motivo da finalização, fechar diretamente
      handleClosingReasonConfirm(null)
    }
  }

  const handleClosingReasonConfirm = async closingReasonId => {
    setClosingReasonModalOpen(false)
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: 'closed',
        closingReasonId: closingReasonId,
        userId: user?.id
      })
    } catch (err) {
      toastError(err)
    }
  }

  const handleClosingReasonCancel = () => {
    setClosingReasonModalOpen(false)
  }

  return (
    <>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={menuOpen}
        onClose={handleClose}
      >
        <MenuItem onClick={handleOpenScheduleModal}>
          {i18n.t('ticketOptionsMenu.schedule')}
        </MenuItem>
        {(!ticket.isGroup || !showTabGroups || user.profile === 'admin') && (
          <MenuItem onClick={handleOpenTransferModal}>
            {i18n.t('ticketOptionsMenu.transfer')}
          </MenuItem>
        )}
        {(ticket.status === 'open' || ticket.status === 'pending') && (
          <MenuItem onClick={handleOpenClosingReasonModal} disabled={loading}>
            {i18n.t('ticketOptionsMenu.close')}
          </MenuItem>
        )}
        <Can
          role={user.profile}
          perform="ticket-options:deleteTicket"
          yes={() => (
            <MenuItem onClick={handleOpenConfirmationModal}>
              {i18n.t('ticketOptionsMenu.delete')}
            </MenuItem>
          )}
        />
      </Menu>
      <ConfirmationModal
        title={`${i18n.t('ticketOptionsMenu.confirmationModal.title')} #${ticket.id
          } ${ticket.contact.name}?`}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteTicket}
      >
        {i18n.t('ticketOptionsMenu.confirmationModal.message')}
      </ConfirmationModal>
      <TransferTicketModalCustom
        modalOpen={transferTicketModalOpen}
        onClose={handleCloseTransferTicketModal}
        ticketid={ticket.id}
      />
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        aria-labelledby="form-dialog-title"
        contactId={contactId}
      />
      {shouldRequireClosingReason(ticket) && (
        <ClosingReasonModal
          open={closingReasonModalOpen}
          onClose={handleClosingReasonCancel}
          onConfirm={handleClosingReasonConfirm}
          ticket={ticket}
        />
      )}
    </>
  )
}

export default TicketOptionsMenu
